/**
 * Paged history, pulled from the phone.
 *
 * The transcript's recent window comes from the local cache and the live
 * socket; OLDER history is fetched a page at a time from the phone as the user
 * scrolls up - the phone reads its own decrypted store and hands back a page of
 * messages (see `handleHistoryPageRequest` on the mobile side). This is the
 * "history comes from the phone, not the server" half of WhatsApp's model.
 *
 * The transport is the to-device rail, which is request/response but async: we
 * send a request, the phone answers into our mailbox, and a drain delivers it.
 * So a request returns a promise that resolves when the matching page arrives
 * (keyed by channel + cursor), or `null` on timeout - the phone may be offline.
 */
import { api } from './api';
import { getDeviceId } from './device';
import { getSession } from './session';
import { initCrypto, refreshKeys } from '../crypto';
import { encryptOlmTo, queryDevices } from '../crypto/olmSession';
import { setHistoryPageHandler, type HistoryPage } from '../crypto/toDevice';
import { loadChannelMessages, saveChannelMessages, savePlaintext } from '../crypto/store';

const HISTORY_PAGE_REQUEST = 'm.nsibi.history_page_request';

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

const cursorKey = (channelId: string, beforeId?: string | null) =>
  `${channelId}|${beforeId ?? ''}`;

type Pending = { resolve: (p: HistoryPage | null) => void; timer: number };
const pending = new Map<string, Pending>();

// Route incoming pages to whoever is waiting. Registered once at module load;
// idempotent because setHistoryPageHandler just overwrites the single slot.
/** Subscribers notified of every page the phone pushes, even one that arrives
 *  after its request timed out - so a slow phone still populates the view. */
type PageListener = (page: HistoryPage) => void;
const listeners = new Set<PageListener>();
export function onHistoryPage(cb: PageListener): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

setHistoryPageHandler((page) => {
  console.log(
    `[nsibi] history page received: ${page.channel_id?.slice(0, 8)} count=${page.messages?.length ?? 0} more=${page.has_more}`,
  );
  // Persist EVERY page to the local cache immediately - a bulk link-time push
  // sends all channels at once, and pages for a conversation that is neither
  // open nor currently awaited would otherwise be dropped. Caching here means
  // opening that chat later reads it straight from disk.
  void persistPage(page).catch(() => undefined);

  // Resolve the request that asked for this cursor, if it is still waiting...
  const k = cursorKey(page.channel_id, page.before_id);
  const waiter = pending.get(k);
  if (waiter) {
    window.clearTimeout(waiter.timer);
    pending.delete(k);
    waiter.resolve(page);
  }
  // ...but ALWAYS hand the page to subscribers. A page can arrive after the
  // 20s timeout (the phone was asleep and woke on the nudge); dropping it then
  // is what left the transcript empty forever. The open conversation merges it
  // whenever it lands.
  for (const l of listeners) {
    try {
      l(page);
    } catch {
      /* a listener must never break delivery */
    }
  }
});

/** Merge a pushed page into the local transcript cache + plaintext cache, so
 *  it survives whether or not a view is listening for it right now. */
async function persistPage(page: HistoryPage): Promise<void> {
  const rows = (page.messages ?? []) as Array<{ id: string; created_at: string; _plain?: string }>;
  if (rows.length === 0) return;
  const existing = ((await loadChannelMessages(page.channel_id).catch(() => [])) ??
    []) as Array<{ id: string; created_at: string; _plain?: string }>;
  const byId = new Map(existing.map((m) => [m.id, m]));
  for (const r of rows) byId.set(r.id, r);
  const ordered = [...byId.values()].sort(
    (a, b) => Date.parse(a.created_at) - Date.parse(b.created_at),
  );
  await saveChannelMessages(page.channel_id, ordered).catch(() => undefined);
  for (const r of rows) {
    if (r._plain !== undefined) void savePlaintext(r.id, r._plain).catch(() => undefined);
  }
}

/** Kept so App can force the module (and its handler) to load. */
export function installHistoryPaging(): void {
  /* the module side-effect above is the install */
}

/**
 * Ask the phone for one page of `channelId` older than `beforeId` (omit for the
 * newest page). Resolves with the page, or null if no own device answered in
 * time.
 */
export async function requestHistoryPage(
  channelId: string,
  beforeId?: string,
  limit = 30,
): Promise<HistoryPage | null> {
  const me = getSession()?.user.id;
  if (!me) return null;
  // Make sure our own Olm account is up before we try to seal a request to the
  // phone - on a freshly linked browser this can be called before bootstrap
  // finishes, and without it the very first history request silently fails and
  // the transcript sits on "loading" until a retry.
  try {
    await initCrypto();
  } catch {
    return null;
  }
  const myDeviceId = getDeviceId();

  const devices = (await queryDevices([me])).filter((d) => d.deviceId !== myDeviceId);
  if (devices.length === 0) {
    console.warn('[nsibi] history page request: no other device to ask (phone not in key directory)');
    return null;
  }

  const messages: Array<{
    target_user_id: number;
    target_device_id: string;
    message_type: string;
    ciphertext: string;
  }> = [];
  for (const device of devices) {
    const ciphertext = await encryptOlmTo(device, {
      type: HISTORY_PAGE_REQUEST,
      content: { channel_id: channelId, before_id: beforeId ?? null, limit },
    });
    if (!ciphertext) continue;
    messages.push({
      target_user_id: device.userId,
      target_device_id: device.deviceId,
      message_type: HISTORY_PAGE_REQUEST,
      ciphertext,
    });
  }
  if (messages.length === 0) return null;

  const k = cursorKey(channelId, beforeId);
  // A newer request for the same cursor supersedes an older one.
  const existing = pending.get(k);
  if (existing) {
    window.clearTimeout(existing.timer);
    existing.resolve(null);
    pending.delete(k);
  }

  const result = new Promise<HistoryPage | null>((resolve) => {
    const timer = window.setTimeout(() => {
      pending.delete(k);
      console.warn(
        `[nsibi] history page request timed out for ${channelId.slice(0, 8)} (phone did not answer)`,
      );
      resolve(null);
    }, 20000);
    pending.set(k, { resolve, timer });
  });
  console.log(
    `[nsibi] history page requested: ${channelId.slice(0, 8)} before=${beforeId?.slice(0, 8) ?? 'newest'} -> ${messages.length} device(s)`,
  );

  await api('/v1/sendToDevice/', {
    method: 'POST',
    body: { sender_device_id: myDeviceId, messages },
  });

  // The answer arrives in our mailbox; a `to_device` nudge normally drains it,
  // but poll a few times too so a quiet socket cannot leave the page hanging
  // until the 20s timeout.
  void (async () => {
    for (const delay of [800, 1500, 2500, 4000]) {
      await sleep(delay);
      if (!pending.has(k)) return;
      await refreshKeys().catch(() => 0);
    }
  })();

  return result;
}
